// Copy text that must first be produced asynchronously (an API round-trip)
// without losing the click's user-activation window. Safari only honors
// clipboard writes triggered during a user gesture: awaiting the network and
// then calling writeText() rejects with NotAllowedError once the activation
// window closes (bug report #207 — "Copy join link" dead for Safari users).
// ClipboardItem exists for exactly this: navigator.clipboard.write() is
// called synchronously inside the gesture with a promised payload that the
// browser awaits itself. Engines without ClipboardItem keep transient
// activation across an await, so the resolve-then-writeText fallback is safe
// there.
//
// Must be called synchronously from the user-gesture handler — deferring the
// call (setTimeout, mutation callbacks) recreates the Safari failure.
export async function copyAsyncText(
  produceText: () => Promise<string>,
): Promise<'copied' | 'failed'> {
  if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
    return 'failed';
  }
  try {
    if (typeof ClipboardItem !== 'undefined') {
      const payload = produceText().then(
        (text) => new Blob([text], { type: 'text/plain' }),
      );
      const write = navigator.clipboard.write([
        new ClipboardItem({ 'text/plain': payload }),
      ]);
      // Await both: engines reject write() when the payload rejects, but the
      // producer's failure must map to 'failed' even where write() resolves
      // without consuming the payload.
      await Promise.all([write, payload]);
    } else {
      await navigator.clipboard.writeText(await produceText());
    }
    return 'copied';
  } catch {
    return 'failed';
  }
}
